import { Routes } from '@angular/router';
import { isAuthorizedGuard, isNotAuthorizedGuard } from '../auth/auth.guard';
import { LoginComponent } from '../login/login.component';
import { Home } from '../home/home';
import { CmsBannerCategory } from '../home/cms/banner/banner.models';
import { userRoleGuard } from '../auth/user-role-guard';
import { environment } from '../environments/environment';

export interface AppRoutesModel {
  jobOffers: string;
  cms: string;
  home: string;
  login: string;
  management: string;
  companies: string;
  international: string;
  banner: string;
  prize: string;
  recruitmentSteps: string;
  recruitmentTeam: string;
  benefits: string;
  socialInvolvement: string;
  jobOffersListing: string;
  jobIntegrationListing: string;
  newJobOffer: string;
  logisticCenter: string;
  technology: string;
  testimonials: string;
  knowledgeBase: string;
}

export const AppRoutes: AppRoutesModel = {
  jobOffers: "job-offers",
  jobOffersListing: "listing",
  jobIntegrationListing: "integration-listing",
  newJobOffer: "new",
  cms: "cms",
  login: "login",
  home: "",
  management: "management",
  companies: "companies",
  international: "international",
  banner: "banner",
  prize: "prize",
  recruitmentSteps: "recruitment-steps",
  recruitmentTeam: "recruitment-team",
  benefits: "benefits",
  socialInvolvement: "social-involvement",
  logisticCenter: "logistic-center",
  technology: "technology",
  testimonials: "testimonials",
  knowledgeBase: "knowledge-base"
}

export const routes: Routes = [
  {
    path: AppRoutes.login,
    component: LoginComponent,
    canActivate: [isNotAuthorizedGuard],
    title: "pageTitles.login",
  },
  {
    path: AppRoutes.home,
    component: Home,
    title: "pageTitles.home",
    canActivate: [isAuthorizedGuard],
    children: [
      {
        path: `${AppRoutes.jobOffers}/${AppRoutes.jobIntegrationListing}`,
        loadComponent: () => import("../home/job-offers/job-integration-listing/job-integration-listing").then((m) => m.JobIntegrationListing),
        title: "pageTitles.cms.jobIntegrationListing",
        canActivate: [userRoleGuard("ROLE_ADMIN")]
      },
      {
        path: `${AppRoutes.jobOffers}/${AppRoutes.jobOffersListing}`,
        loadComponent: () => import("../home/job-offers/job-listing/job-listing").then((m) => m.JobListing),
        title: "pageTitles.cms.jobOffersListing"
      },
      {
        path: `${AppRoutes.jobOffers}/${AppRoutes.newJobOffer}`,
        loadComponent: () => import("../home/job-offers/job-details/job-details").then((m) => m.JobDetails),
        title: "pageTitles.cms.newJobOffer"
      },
      {
        path: `${AppRoutes.jobOffers}/${AppRoutes.jobOffersListing}/:id`,
        loadComponent: () => import("../home/job-offers/job-details/job-details").then((m) => m.JobDetails),
        title: "pageTitles.cms.jobOfferDetails"
      },
      {
        path: `${AppRoutes.cms}/${AppRoutes.testimonials}`,
        loadComponent: () => import("../home/cms/testimonials/testimonials").then((m) => m.Testimonials),
        title: "pageTitles.cms.testimonials",
        canActivate: [userRoleGuard("ROLE_ADMIN")]
      },
      {
        path: `${AppRoutes.cms}/${AppRoutes.banner}`,
        loadComponent: () => import("../home/cms/banner/banner").then((m) => m.Banner),
        title: "pageTitles.cms.banner",
        canActivate: [userRoleGuard("ROLE_ADMIN")],
        data: {
          category: CmsBannerCategory.GENERAL,
        }
      },
      {
        path: `${AppRoutes.cms}/${AppRoutes.logisticCenter}`,
        loadComponent: () => import("../home/cms/banner/banner").then((m) => m.Banner),
        title: "pageTitles.cms.logisticCenter",
        canActivate: [userRoleGuard("ROLE_ADMIN")],
        data: {
          category: CmsBannerCategory.LOGISTIC_CENTER,
        }
      },
      {
        path: `${AppRoutes.cms}/${AppRoutes.prize}`,
        loadComponent: () => import("../home/cms/prize/prize").then((m) => m.Prize),
        title: "pageTitles.cms.prize",
        canActivate: [userRoleGuard("ROLE_ADMIN")]
      },
      {
        path: `${AppRoutes.cms}/${AppRoutes.technology}`,
        loadComponent: () => import("../home/cms/technology/technology").then((m) => m.Technology),
        title: "pageTitles.cms.technology",
        canActivate: [userRoleGuard("ROLE_ADMIN")]
      },
      {
        path: `${AppRoutes.cms}/${AppRoutes.benefits}`,
        loadComponent: () => import("../home/cms/benefits/benefits").then((m) => m.Benefits),
        title: "pageTitles.cms.benefits",
        canActivate: [userRoleGuard("ROLE_ADMIN")]
      },
      {
        path: `${AppRoutes.cms}/${AppRoutes.recruitmentTeam}`,
        loadComponent: () => import("../home/cms/recruitment/recruitment-team/recruitment-team").then((m) => m.RecruitmentTeam),
        title: "pageTitles.cms.recruitmentTeam",
        canActivate: [userRoleGuard("ROLE_ADMIN")]
      },
      {
        path: `${AppRoutes.cms}/${AppRoutes.recruitmentSteps}`,
        loadComponent: () => import("../home/cms/recruitment/recruitment-steps/recruitment-steps").then((m) => m.RecruitmentSteps),
        title: "pageTitles.cms.recruitmentSteps",
        canActivate: [userRoleGuard("ROLE_ADMIN")]
      },
      {
        path: `${AppRoutes.cms}/${AppRoutes.management}`,
        loadComponent: () => import("../home/cms/management/management").then((m) => m.Management),
        title: "pageTitles.cms.management",
        canActivate: [userRoleGuard("ROLE_ADMIN")]
      },
      {
        path: `${AppRoutes.cms}/${AppRoutes.companies}`,
        loadComponent: () => import("../home/cms/companies/companies").then((m) => m.Companies),
        title: "pageTitles.cms.companies",
        canActivate: [userRoleGuard("ROLE_ADMIN")]
      },
      {
        path: `${AppRoutes.cms}/${AppRoutes.socialInvolvement}`,
        loadComponent: () => import("../home/cms/social-involvement/social-involvement").then((m) => m.SocialInvolvement),
        title: "pageTitles.cms.socialInvolvement",
        canActivate: [userRoleGuard("ROLE_ADMIN")]
      },
      {
        path: `${AppRoutes.knowledgeBase}`,
        canMatch: [() => environment.knowledgeBaseEnabled],
        loadComponent: () => import("../home/knowledge-base/knowledge-base").then((m) => m.KnowledgeBase),
        title: "pageTitles.knowledgeBase.main",
        canActivate: [userRoleGuard("ROLE_ADMIN")]
      },
    ]
  },
  {
    path: "**",
    redirectTo: AppRoutes.login
  }
];
